import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Close';

export default function ReusablePDFUploadComponent({
  selectedFile,
  setSelectedFile,
  handleFileChange,
  disabled = false,
  errorMsg,
  error,
  helperText
}) {
  const fileInputRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPdfUrl(null);
    }
  }, [selectedFile]);

  const triggerFileInput = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const processFile = (file) => {
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      handleFileChange({ target: { files: [file] } });
    } else {
      setSelectedFile(null);
      alert('Please upload a valid PDF file.');
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
      handleFileChange({ target: { files: [file] } });
    } else {
      setSelectedFile(null);
      alert("Please upload a valid PDF file.");
    }
    event.target.value = null;
  };

  const handleDelete = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;

    const file = e.dataTransfer.files[0];
    processFile(file);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        onClick={!selectedFile ? triggerFileInput : undefined}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: error ? '2px dashed #d32f2f' : isDragging  ? '2px dashed #1976d2' : '2px dashed rgba(0,0,0,0.2)',
          borderRadius: '8px',
          padding: selectedFile ? 0 : '3rem',
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : selectedFile ? 'default' : 'pointer',
          backgroundColor: isDragging ? '#E3F2FD' : '#F9F9F9',
          position: 'relative',
          overflow: 'hidden',
          height: selectedFile ? '300px' : '280px',
          display: !selectedFile ? 'flex' : 'block',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <input
          type="file"
          accept="application/pdf"
          onChange={handleFileInputChange}
          ref={fileInputRef}
          disabled={disabled}
          style={{ display: 'none' }}
        />

        {selectedFile && pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="PDF Preview"
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        ) : (
          <>
            <UploadFileIcon fontSize="large" color="primary" />
            <Typography variant="body2" color="#626478" sx={{ mt: 1, fontSize: '16px' }}>
              Drag and drop your PDF here, or click to browse
            </Typography>
            <Typography variant="caption" color="#A6A6A6" sx={{ fontSize: '14px' }}>
              PDF (Max. 5MB)
            </Typography>
            {errorMsg && (
              <Typography variant="caption" color="#F14950" sx={{ mt: 0.5, fontSize: '14px' }}>
                {errorMsg}
              </Typography>
            )}
          </>
        )}
      </Box>

      {selectedFile && (
        <Box
          mt={1.5}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f5f5f5',
            padding: '10px 16px',
            borderRadius: '4px',
          }}
        >
          <Typography variant="body2">{selectedFile.name}</Typography>
          <IconButton size="small" onClick={handleDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
